// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AccordionSectionHeader, {
    AccordionSectionHeaderProps,
} from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import AccordionSectionPanel from "@insite/mobius/AccordionSection/AccordionSectionPanel";
import { IconWrapper } from "@insite/mobius/Icon";
import { StyledClickable } from "@insite/mobius/Link";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import Minus from "@mcmc/XDL/Icons/Minus";
import Plus from "@mcmc/XDL/Icons/Plus";
import { xBaseTheme } from "@mcmc/XDL/types";
import { styledScrollbar } from "@mcmc/XDL/Utilities";
import { css } from "styled-components";

export default {
    defaultProps: {
        css: css`
            background: ${getColor("primary.contrast")};
            box-shadow: ${({ theme }) => get(theme, "shadows.1")};
            margin-bottom: 20px;
            ${AccordionSectionHeader}:first-child {
                border-top: 3px solid ${getColor("primary")};

                @media print {
                    border-top-width: 0;
                }
            }
        `,
    },
    sectionDefaultProps: {
        headerProps: {
            css: css`
                background: inherit;
                ${({ headingLevel = 0 }: AccordionSectionHeaderProps) =>
                    headingLevel <= 1
                        ? css`
                              padding-left: 20px;
                              padding-right: 20px;
                          `
                        : ""}
                ${/* sc-selector */ AccordionSectionPanel} + & button,button {
                    &,
                    &:focus {
                        background: inherit;
                        border-top: none;
                        border-bottom: 1px solid ${getColor("common.border")};
                        border-left: none;
                        border-right: none;
                        padding: 18px;
                        @media print {
                            display: flex !important;
                        }
                        ${({ headingLevel = 0, expanded }: AccordionSectionHeaderProps) =>
                            headingLevel <= 1
                                ? css`
                                      padding-left: 0;
                                      padding-right: 0;
                                      ${!expanded &&
                                      css`
                                          border-color: transparent;
                                      `}
                                  `
                                : ""}
                    }

                    ${StyledClickable},${StyledClickable}:focus {
                        padding: 0;
                    }

                    &:focus ${IconWrapper},${StyledClickable}:focus {
                        outline-color: ${getProp("theme.focus.color", "#09f")};
                        outline-style: ${getProp("theme.focus.style", "solid")};
                        outline-width: ${getProp("theme.focus.width", "2px")};
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                border: none;
                padding: 25px 20px 20px 20px;
                border-bottom: 1px solid ${getColor("common.border")};
                &:last-child {
                    border: none;
                }
                ${styledScrollbar()};
                overflow: visible;
            `,
        },
        titleTypographyProps: {
            size: "18px",
            weight: 400,
            lineHeight: "21px",
        },
        collapseIconProps: {
            src: Minus,
        },
        expandIconProps: {
            src: Plus,
        },
        toggleIconProps: {
            width: 12,
            height: 12,
            size: 12,
            css: css`
                @media print {
                    position: absolute;
                    left: 8px;
                    bottom: 10px;
                }
            `,
        },
    },
} as xBaseTheme["accordion"];
